import type { StatusTone } from "./primitives";

/**
 * The counts a list opens with, one box each: "47 engagements", "0 running",
 * "29 completed".
 *
 * They were boxes, became one line of facts (#124) to save height on a phone,
 * and are boxes again because the line read as small print — the numbers are
 * the first thing anybody looking at the firm's work wants, and a box makes
 * each one a thing to look at and to press. Two columns on a phone, four on
 * a tablet, and all seven in one row from a laptop up — a second row of three
 * read as two lists, and boxes sized for four made the counts shout. The
 * number takes its size from the box, and a label that does not fit wraps
 * rather than losing its end: "awaiting a sco…" is not a label.
 *
 * **A box with a destination is a button** and filters the list to what it
 * counts, the way the line did; the one the list is filtered to is outlined.
 * A status tone is a dot beside the label, never a colour on the number:
 * status colours are marks, not text.
 */
export interface Stat {
  value: string;
  label: string;
  hint?: string;
  tone?: StatusTone;
  title?: string;
  onClick?: () => void;
  /** The list is showing what this box counts. */
  active?: boolean;
}

const DOT: Record<StatusTone, string> = {
  good: "bg-good",
  warning: "bg-warning",
  serious: "bg-serious",
  critical: "bg-critical",
  neutral: "bg-baseline",
};

export function StatTiles({ items }: { items: Stat[] }) {
  return (
    <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
      {items.map((stat) => (
        <li key={stat.label} className="min-w-0">
          <Tile stat={stat} />
        </li>
      ))}
    </ul>
  );
}

function Tile({ stat }: { stat: Stat }) {
  const body = (
    <>
      <span className="label flex items-start gap-1.5 text-muted">
        {stat.tone && stat.tone !== "neutral" && (
          <span aria-hidden className={`mt-1 size-1.5 shrink-0 rounded-full ${DOT[stat.tone]}`} />
        )}
        <span className="min-w-0">{stat.label}</span>
      </span>
      {/* Sized by the box, not the window: a seventh of a 1024px screen has
          to hold "$1,209.49", and a seventh of a wide one should not shout it. */}
      <span className="mt-1.5 block font-mono text-lg tabular-nums leading-none text-ink @[9rem]:text-xl @[11.5rem]:text-2xl">
        {stat.value}
      </span>
      {stat.hint && <span className="mt-1.5 block text-xs text-ink-2">{stat.hint}</span>}
    </>
  );
  // A column from the top, not `block`: a <button> centres what it holds,
  // so the boxes that filter sat lower than the two that do not, and in one
  // row the numbers no longer lined up.
  const shell = `@container card flex h-full w-full flex-col justify-start px-3 py-3 text-left ${
    stat.active ? "border-accent" : ""
  }`;
  if (stat.onClick) {
    return (
      <button
        type="button"
        onClick={stat.onClick}
        title={stat.title}
        aria-pressed={stat.active}
        className={`${shell} transition-colors hover:border-line-strong hover:bg-surface-2`}
      >
        {body}
      </button>
    );
  }
  return (
    <div className={shell} title={stat.title}>
      {body}
    </div>
  );
}
